import _ from 'lodash'
import store from '@ps/store'
import {
  isVideo,
  isAudio,
  isDrawLineNode,
  isDrawLineAreaNode,
  isConnectDrawLineNode,
  isEraseCustomJudgeAreasNode,
  isEraseNodeChild,
  isEraseNode,
  isMaskComponentNodeChild,
  isMaskComponentNode,
  isCarouselNodeChild,
  isCarouselNode,
  isEmptyNode,
} from '@ps/bridge/qc/base'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  getMirrorNodeAttrs,
  reactive,
} from '@ps/store/workbench/game/mirror-node'
import Vue from 'vue'
import qc from 'qc'
import * as nodeUtils from '@ps/store/workbench/common/node-utils'
import {
  addChildAt,
  resetAndStopAnimation,
  replaceAnimationUuid,
} from '@ps/bridge/qc/node'
import { Event, EventBus } from '@ps/utils/event-bus'
import { filter, findByUuid, getDescendants } from '@ps/bridge/qc/node/utils'
import * as videoScript from '@ps/bridge/qc/scripts/video'
import {
  AstActionItem,
  AstConditionItem,
  AstEventItem,
} from '@ps/bridge/qc/scripts/editor'
import { generateUUID } from '@ps/utils/game'
import nodePool from '@ps/bridge/qc/node-pool'
import { getVpAstFromSceneNode } from '@ps/bridge/qc/vp'
import {
  DragConditionType,
  DrawLineConditionType,
  EraseConditionType,
  getVpConditionConf,
} from '@ps/bridge/qc/vp/config'
import { isGlobalScene } from '@ps/bridge/qc/scripts/scene-config'
import { SceneType } from '@ps/bridge/qc/scripts/scene-config'
import { pairItem, areaItem } from './draw-line'
import { GeneralMode } from '@ps/types/workbench/stage.type'
import { getFirstLevelNode } from '@ps/store/workbench/game/actions/drag-target-area'
import { updateCurrentEraseCustomLayer } from '@ps/views/workbench/right-pane/erase/layer/index'
import {
  eraseCanvasHandler,
  eventItem,
  recoverNodeBindCustomEvents,
  removeNodeBindEraseCustomEvents,
} from './erase'
import { updateShapeBorderStyle } from '@ps/store/workbench/stage/utils/helper'
import { maskComponentCanvasHandler, updateMaskCustomSubGroup } from './mask'
import { scripts } from '@ps/bridge/qc/scripts'
import {
  findChildByRouteIndexInComponentNode,
  getRouteIndexByNodeInComponentNode,
} from './play-component'
import { carouselComponentCanvasHandler } from '@ps/bridge/qc/node/carousel'
import { LayerColor } from '@ps/types/workbench/layer'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { isIvPage } from '@ps/router/config'
import { addEventItem } from '@ps/store/interactive-video/event/event'
import { EventType } from '@ps/bridge/qc/vp/config'
import {
  getPoolItemMainPath,
  deleteResourceAction,
} from '@ps/bridge/resource/pool/index'
import { getSameRouteNodeBetweenScene } from '@ps/workbench/copy-paste/event'
import { getParamLabelUI } from '@ps/store/workbench/visual-program/utils'

export type eventsToNodeItem<T> = {
  astIndex: number
  index: number
  value: T
  sceneId: string
}
/**
 * 获取新的节点列表
 * @param uuids 当前节点列表
 * @param root 根节点
 */
const getNodesUseUuid = (
  uuids: string[],
  root: qc.Node,
): qc.Node[] | undefined => {
  if (!uuids) return
  return filter(root, (item) => uuids.some((j) => j === item.uuid))
}

export type UpdateActiveNodeActionValue = {
  oldValue: {
    uuids: string[] // 上一个选中的节点列表
    uuid: string // 上一个选中的节点id
  }
  newValue: {
    uuids: string[] // 当前选中的节点列表
    uuid: string // 当前选中的节点id
  }
}

/**
 * 激活当前节前
 * @param uuid 当前节点id
 * @param nodes qc节点列表
 */
const updateActiveNode = ({
  uuid,
  nodes,
}: {
  uuid: string
  nodes: qc.Node[]
}) => {
  if (!uuid) return
  const node = nodes.find((item) => item.uuid === uuid)
  const gameState = store.state.workspace.game
  if (node) {
    gameState.activeNode = reactive(
      node,
      (key: string, value: boolean | number | string) => {
        if (gameState.mirrorNode) {
          Vue.set(gameState.mirrorNode, key, value)
        } else {
          console.log(
            '找不到 mirrorNode',
            gameState.activeNode,
            gameState.mirrorNode,
          )
        }
      },
    )
    // 只获取必要的一些属性，如果有视频节点的话，深拷贝的时候很卡
    gameState.mirrorNode = getMirrorNodeAttrs(gameState.activeNode)
    // 根据节点图层的类型更新边框颜色
    updateShapeBorderStyle(node)
  }
}

export const getUpdateActiveNodeAction = (
  value: UpdateActiveNodeActionValue,
) => {
  const gameState = store.state.workspace.game
  return {
    execute: ({ newValue }: UpdateActiveNodeActionValue) => {
      const { uuids, uuid } = newValue
      gameState.activeNodeList = getNodesUseUuid(uuids, gameIframe.UIRoot)
      if (gameState.activeNodeList) {
        const nodes: qc.Node[] = gameState.activeNodeList
        updateActiveNode({ nodes, uuid })
        // 点击选中图层，暂停资源动画
        nodes.forEach((node) => {
          resetAndStopAnimation(node)
        })
        if (
          !(
            (nodes.length === 1 && isDrawLineNode(nodes[0])) ||
            isDrawLineAreaNode(nodes[0])
          )
        ) {
          store.commit('workspace/stage/updateDrawLineMode', GeneralMode.None)
          store.commit(
            'workspace/stage/updateDrawLineAreaMode',
            GeneralMode.None,
          )
        }
        store.commit('workspace/rightPane/switchToNodeConfigTab')
        // 擦除组件屏蔽group级的子节点的画布操作
        if (nodes.some((node) => isEraseNodeChild(node))) {
          eraseCanvasHandler(nodes[0])
        }
        // 遮罩组件屏蔽group级的子节点的画布操作
        if (nodes.some((node) => isMaskComponentNodeChild(node))) {
          maskComponentCanvasHandler(nodes[0])
        }
        // 轮播组件屏蔽组件动态节点画布操作
        if (nodes.some((node) => isCarouselNodeChild(node))) {
          carouselComponentCanvasHandler(nodes[0])
        }
      }
    },
    undo: ({ oldValue }: UpdateActiveNodeActionValue) => {
      const { uuids, uuid } = oldValue
      gameState.activeNodeList = getNodesUseUuid(uuids, gameIframe.UIRoot)

      if (uuid) {
        const nodes: qc.Node[] = gameState.activeNodeList
        updateActiveNode({ nodes, uuid })
        // 点击选中图层，暂停资源动画
        nodes.forEach((node) => {
          resetAndStopAnimation(node)
        })
        store.commit('workspace/rightPane/switchToNodeConfigTab')
      } else {
        gameState.activeNode = null
        gameState.mirrorNode = null
      }
    },
    value,
    methodName: 'getUpdateActiveNodeAction',
  }
}

export type SetStyleActionValue = {
  newValue: {
    nodes: {
      uuid: string
      [key: string]: number | string | boolean
    }[]
  }
  oldValue: {
    nodes: {
      uuid: string
      [key: string]: number | string | boolean
    }[]
  }
}
export const getSetStyleAction = (value: SetStyleActionValue) => {
  return {
    value,
    execute: ({ newValue }: SetStyleActionValue) => {
      const { nodes } = newValue
      nodeUtils.updateNodesStyle({ nodes })
    },
    undo: ({ oldValue }: SetStyleActionValue) => {
      const { nodes } = oldValue
      nodeUtils.updateNodesStyle({ nodes })
    },
    methodName: 'getSetStyleAction',
  }
}

export type AddNodeActionValue = {
  newValue: {
    node: qc.Node
    parent: qc.Node
    index: number
    realNode?: qc.Node
  }
  oldValue: {
    node: qc.Node
    activeUuid: string
    realNode?: qc.Node
  }
}
export const getAddNodeAction = (value: AddNodeActionValue) => {
  return {
    execute: ({ newValue }: AddNodeActionValue) => {
      const { node, parent, index, realNode } = newValue
      const currentSceneNode =
        store.state.workspace.sceneManager.currentSceneNode
      // 新增节点的时候在 recordHistory 之前已经增加节点了，所以需要判断节点树上是否有该节点
      if (!findByUuid(node.uuid)) {
        addChildAt({
          node,
          parent,
          index,
        })
      }
      if (isDrawLineAreaNode(node)) {
        store.dispatch('workspace/game/drawLine/addNodeForPairsData', node)
        EventBus.$emit(Event.ADD_DRAW_LINE_AREA_NODE)
      }
      if (!isEraseCustomJudgeAreasNode(node)) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: realNode || node,
          record: false,
        })
      }
      if (isIvPage() && realNode && isEmptyNode(realNode)) {
        // 互动视频编辑器的手势区域添加时自动加上install事件
        addEventItem({
          conditionNode: realNode,
          actionNode: realNode,
          handCmd: EventType.down,
          action: 'vpInstall',
          sceneNode: currentSceneNode,
        })
      }
      EventBus.$emit(Event.ADD_GAME_OBJECT, node.uuid)
    },
    undo: ({ oldValue }: AddNodeActionValue) => {
      const { node, realNode, activeUuid } = oldValue
      if (isIvPage() && realNode) {
        // 互动视频有些节点会默认添加事件，undo时需要删除事件
        store.dispatch('workspace/game/visualProgram/removeNode', {
          uuid: realNode.uuid,
        })
      }
      node.removeSelf()
      if (isConnectDrawLineNode(node) || isDrawLineAreaNode(node)) {
        store.dispatch('workspace/game/drawLine/initData')
      }
      // 如果undo的时候记录了要激活的节点，则激活该节点
      const activeNode = findByUuid(activeUuid)
      if (activeNode) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: activeNode,
          record: false,
        })
      } else {
        store.dispatch('workspace/game/clearActiveNode', {
          record: false,
        })
      }
    },
    value,
    methodName: 'getAddNodeAction',
  }
}

export type ClearActiveNodeActionValue = {
  oldValue: {
    uuid: string
    uuids: string[]
  }
}
export const getClearActiveNodeAction = (value: ClearActiveNodeActionValue) => {
  const gameState = store.state.workspace.game
  const currentSceneNode =
    store.state.workspace.game.sceneManager.currentSceneNode
  const clear = () => {
    if (gameState.activeNode) {
      resetAndStopAnimation(gameState.activeNode)
      // 回复虚拟关联，清理虚拟节点
      if (store.state.workspace.animation.tweenPositionAreaActived) {
        store.dispatch(
          'workspace/stage/enableLinkageNode',
          gameState.activeNode,
        )
      }
      store.dispatch('workspace/stage/destroyVirtualNodeGhost')
    }
    gameState.activeNode = null
    gameState.activeNodeList = []
    // 置空连线组件显示状态
    store.commit('workspace/stage/updateDrawLineMode', GeneralMode.None)
    store.commit('workspace/stage/updateDrawLineAreaMode', GeneralMode.None)
    if (isIvPage()) {
      store.dispatch('workspace/rightPane/clearTab')
    } else {
      if (currentSceneNode && isGlobalScene(currentSceneNode)) {
        store.dispatch('workspace/rightPane/switchToGlobalConfigTab')
      } else {
        store.dispatch('workspace/rightPane/switchToStageConfigTab')
      }
    }
  }
  return {
    execute: () => {
      clear()
    },
    // undo 时不要调用 updateActiveNode action，因为那里处理了快捷键的逻辑，放在一起不好维护
    undo: ({ oldValue }: ClearActiveNodeActionValue) => {
      const { uuid, uuids } = oldValue
      store.commit('workspace/game/selectNode', { uuid, uuids })
    },
    value,
    methodName: 'getClearActiveNodeAction',
  }
}

export type RemoveNodesActionValue = {
  newValue: {
    nodes: qc.Node[]
    activeUuids: string[]
    activeUuid: string
    poolItem?: any
  }
  oldValue: {
    nodes: qc.Node[]
    // 子节点被删除后，再通过被删除的节点去获取 parent，返回 null
    parents: qc.Node[]
    // 子节点被删除后，再通过父节点无法再获取到，所以需要单独加索引列表
    indexs: number[]
    activeUuids: string[]
    activeUuid: string
    eventsToNode: {
      condition: eventsToNodeItem<AstConditionItem>[]
      action: eventsToNodeItem<AstActionItem>[]
    }
    ast: AstEventItem[]
    pairsData: pairItem[]
    areasData: areaItem[]
    eraseCustomEvents: eventItem[]
    poolItem?: any
  }
}
const removeChild = (node: qc.Node) => {
  // 移除节点时，不可以调用 destroy，销毁后历史记录功能异常
  if (node && node.parent) {
    // 视频组件特殊处理，onDisable 方法会暂停视频播放
    if (isVideo(node)) {
      const videoScriptConfig = videoScript.getScript(node)
      if (videoScriptConfig) {
        _.isFunction(videoScriptConfig.onDisable) &&
          videoScriptConfig.onDisable()
      }
    }
    const parentNode = node.parent
    node.parent.removeChild(node)

    // setVideosManager 需要在节点移除后调用
    if (isVideo(node)) {
      store.dispatch('workspace/game/video/setVideosManager')
    }
    // 连线组件处理
    if (isDrawLineAreaNode(node)) {
      store.dispatch('workspace/game/drawLine/removeNodeForPairsData', node)
      store.dispatch('workspace/game/updateActiveNode', {
        node: parentNode,
      })
      store.dispatch(
        'workspace/game/visualProgram/removeDrawLineAreaForEvent',
        { node },
      )
    } else if (isConnectDrawLineNode(node)) {
      store.dispatch('workspace/game/drawLine/removeNodeForPairsData', node)
    }
  }
}

export const removeNodes = ({
  nodes,
  activeUuids,
}: {
  nodes: qc.Node[]
  activeUuids: string[]
}) => {
  // 删除前判定是否2d玩法组件的子节点
  let is2dComponentChildNode = false
  if (
    nodes.every(
      (node) => isEraseNodeChild(node) || isMaskComponentNodeChild(node),
    )
  ) {
    is2dComponentChildNode = true
  }
  const hasCarouselChild = nodes.some((node) => isCarouselNodeChild(node))
  nodes.forEach((node) => {
    removeChild(node)
    EventBus.$emit(Event.REMOVE_GAME_OBJECT, node.uuid)
  })

  // 更新节点后通知图层模块刷新
  EventBus.$emit(Event.REFRESHLAYER)
  // 删除包含轮播组件节点
  if (hasCarouselChild) {
    EventBus.$emit(Event.REFRESHCAROUSEL)
  }
  // 删除连线组件子区域节点特殊处理
  if (nodes.every((node) => isDrawLineAreaNode(node))) return
  // 擦除组件的子节点特殊处理
  if (is2dComponentChildNode) return
  // 删除2d玩法组件后选中场景节点
  if (
    nodes.some(
      (node) =>
        isEraseNode(node) || isDrawLineNode(node) || isMaskComponentNode(node),
    )
  ) {
    store.commit('workspace/game/selectNode', {
      uuid: '',
      uuids: [],
    })
  }
  // 删除节点是否是当前激活节点中的一个
  const hasDeleteActiveNode = activeUuids.some((uuid) =>
    nodes.some((node) => node.uuid === uuid),
  )
  if (hasDeleteActiveNode) {
    // 删除后剩余的激活节点列表
    const leftActiveUuids = activeUuids.filter((uuid) =>
      nodes.every((node) => node.uuid !== uuid),
    )
    if (_.isEmpty(leftActiveUuids)) {
      store.commit('workspace/game/selectNode', {
        uuid: '',
        uuids: [],
      })
    } else {
      store.commit('workspace/game/selectNode', {
        uuid: leftActiveUuids[leftActiveUuids.length - 1],
        uuids: leftActiveUuids,
      })
    }
  }
}
export const getRemoveNodesAction = (value: RemoveNodesActionValue) => {
  return {
    execute: ({ newValue }: RemoveNodesActionValue) => {
      const { nodes, activeUuids, poolItem } = newValue
      updateMaskCustomSubGroup(nodes, 'remove')
      removeNodeBindEraseCustomEvents(nodes)
      removeNodes({ nodes, activeUuids })
      updateCurrentEraseCustomLayer(nodes, 'remove')
      if (poolItem) {
        const paths = getPoolItemMainPath(poolItem)
        paths.forEach((path) => {
          deleteResourceAction.addResource(path)
        })
      }
      EventBus.$emit(Event.RESOURCE_POOL_REFRESH)
    },
    undo: ({ oldValue }: RemoveNodesActionValue) => {
      const {
        nodes,
        parents,
        indexs,
        activeUuid,
        activeUuids,
        eventsToNode,
        ast,
        pairsData,
        areasData,
        eraseCustomEvents,
        poolItem,
      } = oldValue
      store.dispatch('workspace/game/visualProgram/recoverNode', {
        eventsToNode,
      })

      nodes.forEach((node, index) => {
        const parent = parents[index]
        addChildAt({ node, parent, index: indexs[index] })
      })
      updateCurrentEraseCustomLayer(nodes, 'add')
      updateMaskCustomSubGroup(nodes, 'add')
      // 恢复包含轮播组件节点
      if (nodes.some((node) => isCarouselNodeChild(node))) {
        EventBus.$emit(Event.REFRESHCAROUSEL)
      }
      store.commit('workspace/game/selectNode', {
        uuid: activeUuid,
        uuids: activeUuids,
      })
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: ast,
      })
      store.commit('workspace/game/drawLine/updatePairsData', { pairsData })
      store.commit('workspace/game/drawLine/updateAreasData', { areasData })
      recoverNodeBindCustomEvents(nodes[0] as qc.Node, eraseCustomEvents)
      if (poolItem) {
        const paths = getPoolItemMainPath(poolItem)
        paths.forEach((path) => {
          deleteResourceAction.removeResource(path)
        })
      }
      EventBus.$emit(Event.RESOURCE_POOL_REFRESH)
      // 更新节点后通知图层模块刷新
      EventBus.$emit(Event.REFRESHLAYER)
      setTimeout(() => {
        store.dispatch('workspace/storyboard/renderEdges')
      }, 100)
    },
    value,
    methodName: 'getRemoveNodesAction',
  }
}

export type LockNodeActionAction = {
  oldValue: {
    uuids: string[]
  }
  newValue: {
    uuids: string[]
  }
}
export const getLockNodeAction = (value: LockNodeActionAction) => {
  const gameState = store.state.workspace.game
  return {
    execute: ({ newValue }: LockNodeActionAction) => {
      const nodes = filter(gameIframe.UIRoot, (node) =>
        newValue.uuids.some((j) => j === node.uuid),
      )
      // 检查需要更新节点的锁定状态，子孙节点根据父节点更新状态进行更新
      const nodesNeedToRemove = nodes.filter(
        (node) => gameState.lockedNodeUuids.indexOf(node.uuid) !== -1,
      )
      const nodesNeedToAdd = nodes.filter(
        (node) => gameState.lockedNodeUuids.indexOf(node.uuid) === -1,
      )

      const uuidsNeedToRemove = _.uniq(
        _.flattenDeep(
          nodesNeedToRemove.map((node) => {
            return [...getDescendants(node).map((item) => item.uuid)]
          }),
        ),
      )
      const uuidsNeedToAdd = _.uniq(
        _.flattenDeep(
          nodesNeedToAdd.map((node) => {
            return [...getDescendants(node).map((item) => item.uuid)]
          }),
        ),
      )
      if (!_.isEmpty(uuidsNeedToAdd)) {
        gameState.lockedNodeUuids = [
          ...uuidsNeedToAdd,
          ...gameState.lockedNodeUuids,
        ]
      }
      if (!_.isEmpty(uuidsNeedToRemove)) {
        gameState.lockedNodeUuids = gameState.lockedNodeUuids.filter(
          (item: string) => uuidsNeedToRemove.every((j) => j !== item),
        )
      }
    },
    undo: ({ oldValue }: LockNodeActionAction) => {
      const { uuids } = oldValue
      gameState.lockedNodeUuids = uuids
    },
    value,
    methodName: 'getLockNodeAction',
  }
}

export type SwitchParentActionValueItem = {
  parent: qc.Node
  nodes: qc.Node[]
  index: number
}
export type SwitchParentActionValue = {
  newValue: SwitchParentActionValueItem
  oldValue: {
    parents: qc.Node[] // 被移动的节点列表对应的父节点
    nodes: qc.Node[] // 被移动的节点列表
    indexs: number[] // 被移动的节点列表对应的在其父节点的索引值
  }
}
export const getSwitchParentAction = (value: SwitchParentActionValue) => {
  const update = ({ parent, nodes, index }: SwitchParentActionValueItem) => {
    nodeUtils.switchParentAndRefresh({
      parent,
      nodes,
      index,
    })
  }

  return {
    execute: ({ newValue }: SwitchParentActionValue) => {
      update(newValue)
      EventBus.$emit(Event.REFRESHLAYER)
    },
    undo: ({ oldValue }: SwitchParentActionValue) => {
      const { nodes, parents, indexs } = oldValue
      nodes.forEach((node, i) => {
        update({
          nodes: [node],
          parent: parents[i],
          index: indexs[i],
        })
      })
      EventBus.$emit(Event.REFRESHLAYER)
    },
    value,
    methodName: 'getSwitchParentAction',
  }
}

export type AddGroupActionValue = {
  newValue: {
    group: qc.Node // 新建的组节点
    nodes: qc.Node[] // 需要移动到组节点的节点
    parent: qc.Node // 组节点的父节点
    index: number
  }
  oldValue: {
    group: qc.Node // 新建的组节点
    nodes: qc.Node[] // 需要移动到组节点的节点
    parents: qc.Node[] // 需要移动到组节点的节点原来的父节点
    indexs: number[] // 需要移动到组节点的节点在原来的父节点上的索引
  }
}
export const getAddGroupAction = (value: AddGroupActionValue) => {
  return {
    execute: ({ newValue }: AddGroupActionValue) => {
      const { group, nodes, parent, index } = newValue
      updateMaskCustomSubGroup([group], 'add')
      if (!findByUuid(group.uuid)) {
        addChildAt({
          node: group,
          parent,
          index,
        })
      }
      if (nodes && nodes.length > 0) {
        nodeUtils.switchParentAndRefresh({
          nodes,
          parent: group,
          index: 0,
        })
        nodeUtils.resetGroupLayoutByNodes({
          group: group,
          nodes: nodes,
        })
      }
    },
    undo: ({ oldValue }: AddGroupActionValue) => {
      const { nodes, parents, indexs, group } = oldValue
      nodes.forEach((node, i) => {
        nodeUtils.switchParentAndRefresh({
          nodes: [node],
          parent: parents[i],
          index: indexs[i],
        })
      })
      updateMaskCustomSubGroup([group], 'remove')
      // 移除组要在 nodes switchParent 之后
      group.removeSelf()
      EventBus.$emit(Event.REFRESHLAYER)
    },
    value,
    methodName: 'getAddGroupAction',
  }
}

export type CloneNodeAction = {
  newValue: {
    originalNodes: qc.Node[]
    nodes: qc.Node[]
    parent: qc.Node
    index: number
    currentSceneEditType: number
  }
  oldValue: {
    nodes: qc.Node[]
    activeUuid: string
  }
}
export const getCloneNodeAction = (value: CloneNodeAction) => {
  return {
    execute: ({ newValue }: CloneNodeAction) => {
      const { nodes, parent, index, originalNodes, currentSceneEditType } =
        newValue

      nodes.forEach((node, j) => {
        // 新增节点的时候在正向操作之前已经增加节点了，所以需要判断节点树上是否有该节点
        if (!findByUuid(node.uuid)) {
          addChildAt({
            node,
            parent,
            index: index + j,
          })
        }
        // 复制节点时,新节点的动画uuid和builtinId需要替换新的
        const nodeList = getDescendants(node)
        nodeList.forEach((item: qc.Node) => {
          replaceAnimationUuid(item)
        })
        // 点击选中图层，暂停资源动画
        resetAndStopAnimation(node)
        originalNodes.forEach((item) => resetAndStopAnimation(item))
        store.dispatch('workspace/game/screenAdapter/adapterHandlerByElement', {
          node: node,
        })
        // 复制粘贴画线节点时,把里面的子节点的name去掉后缀_Copy
        if (isDrawLineNode(node)) {
          node.children.forEach((n) => {
            n.name = n.name.replace(/_Copy$/i, '')
          })
          node.name = node.name.replace(/_Copy$/i, '')
        }
        // 复制粘贴起止区域节点时,名字要改成当前最大序号+1
        if (isDrawLineAreaNode(node)) {
          const maxNumIndex = node.parent.children.length - 2
          if (maxNumIndex > -1) {
            const maxNumNode = node.parent.children[maxNumIndex]
            if (maxNumNode) {
              node.name = String(Number(maxNumNode.name) + 1)
            } else {
              node.name = '1'
            }
          } else {
            node.name = '1'
          }
          store.dispatch('workspace/game/drawLine/copyNodeForPairsData', node)
        }
        if (isEraseNode(node)) {
          // 擦除组件里的粘贴节点命名不用_Copy
          const nodeList = getDescendants(node)
          nodeList.forEach((child) => {
            child.name = child.name.replace(/_Copy[\d]*$/i, '')
          })
          // 自定义判定区域数据联动更新
          const eraseComponent = node.getScript(scripts.erase.name)
          for (const i in eraseComponent.customEvents) {
            const ce = eraseComponent.customEvents[i]
            ce.area = ce.area.map((area: string) => {
              const areaNode = nodePool.find(area)
              const route = getRouteIndexByNodeInComponentNode(areaNode)
              const resNode = findChildByRouteIndexInComponentNode(node, route)
              return resNode.uuid
            })
          }
        }
        EventBus.$emit(Event.ADD_GAME_OBJECT, node.uuid)
      })
      // 复制事件，玩法场景下复制行为不复制节点事件
      if (currentSceneEditType !== SceneType.PLAY) {
        store.dispatch('workspace/copyPaste/pasteEventByCopyNode', {
          targetNodes: nodes,
          originalNodes: originalNodes,
          record: false,
        })
      }
      store.dispatch('workspace/game/updateActiveNode', {
        node: nodes[nodes.length - 1],
        record: false,
      })
      // 复制节点存在轮播组件，刷新轮播组件
      if (nodes.some((node) => isCarouselNode(node))) {
        setTimeout(() => {
          store.dispatch('workspace/game/updateCarouselGroup')
        }, 0)
      }
      EventBus.$emit(Event.REFRESHLAYER)
    },
    undo: ({ oldValue }: CloneNodeAction) => {
      const { nodes, activeUuid } = oldValue
      nodes.forEach((node) => {
        node.removeSelf()
        if (isConnectDrawLineNode(node) || isDrawLineAreaNode(node)) {
          store.dispatch('workspace/game/drawLine/initData')
        }
      })
      // 如果undo的时候记录了要激活的节点，则激活该节点
      const activeNode = findByUuid(activeUuid)
      if (activeNode) {
        store.dispatch('workspace/game/updateActiveNode', {
          node: activeNode,
          record: false,
        })
      } else {
        store.dispatch('workspace/game/clearActiveNode', {
          record: false,
        })
      }
      EventBus.$emit(Event.REFRESHLAYER)
    },
    value,
    methodName: 'getCloneNodeAction',
  }
}

export type PasteNodeStyleActionValue = {
  newValue: {
    styles: Record<string, Record<string, any>>
    oldStyles: Record<string, Record<string, any>>
    nodes: qc.Node[]
  }
  oldValue: {
    // 可粘贴给多个节点
    styles: Record<string, Record<string, any>>
    nodes: qc.Node[]
  }
}
export const getPasteNodeStyleAction = (value: PasteNodeStyleActionValue) => {
  return {
    execute: ({ newValue }: PasteNodeStyleActionValue) => {
      const { styles, oldStyles, nodes } = newValue

      _.each(nodes, (node) => {
        store.dispatch('workspace/game/setStyle', {
          nodes: styles[node.uuid],
          preNodes: oldStyles[node.uuid],
          record: false,
        })
      })
    },
    undo: ({ oldValue }: PasteNodeStyleActionValue) => {
      const { styles, nodes } = oldValue
      _.each(nodes, (node) => {
        store.dispatch('workspace/game/setStyle', {
          nodes: styles[node.uuid],
          record: false,
        })
      })
    },
    value,
    methodName: 'getPasteNodeStyleAction',
  }
}
export type PasteNodeEventActionValue = {
  newValue: {
    copyNodeAst: AstEventItem[]
    targetNodes: qc.Node[]
    sceneNode: qc.Node
    copyScene: qc.Node
  }
  oldValue: {
    targetNodesAst: { [uuid: string]: AstEventItem[] }
    sceneNode: qc.Node
  }
}
export const getPasteNodeEventAction = (value: PasteNodeEventActionValue) => {
  return {
    execute: ({ newValue }: PasteNodeEventActionValue) => {
      const { copyNodeAst, targetNodes, sceneNode, copyScene } = newValue
      const pasteInSameScene = sceneNode.uuid === copyScene.uuid
      const vpAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      const hasEvent = (content: string, nodeId: string) => {
        // 粘贴拖曳到指定区域，自由画线，连线的事件一定要新建事件
        const list = [
          DragConditionType.VPDRAG_END,
          DragConditionType.VPUNDRAG_END,
          DrawLineConditionType.DRAW_START,
          DrawLineConditionType.DRAW_END,
          DrawLineConditionType.FREE_DRAW_START,
          DrawLineConditionType.FREE_DRAW_END,
          EraseConditionType.ERASE_START,
          EraseConditionType.ERASE_UP,
          EraseConditionType.ERASE_REACH_STEP,
          EraseConditionType.ERASE_UN_REACH_STEP,
        ]
        if (list.indexOf(content) > -1) {
          return {
            status: false,
            event: null,
          }
        }
        // 已有带有条件比较的eventItem,需要新建一个eventItem
        for (const event of vpAst) {
          if (
            event.condition[0] &&
            event.condition[0].content === content &&
            event.condition[0].node === nodeId &&
            event.condition.some((item) => item.type === 'compare')
          ) {
            return {
              status: false,
              event: null,
            }
          }
        }
        for (const event of vpAst) {
          if (Array.isArray(event.condition)) {
            for (const condition of event.condition) {
              if (condition.node === nodeId && condition.content === content) {
                return { status: true, event }
              }
            }
          }
        }
        return {
          status: false,
          event: null,
        }
      }
      const replaceTargetNode = (action: AstActionItem) => {
        if (!pasteInSameScene && action.target) {
          const node = nodePool.find(action.node)
          const resNode = getSameRouteNodeBetweenScene(node, sceneNode)
          if (resNode) {
            action.node = resNode.uuid
          } else {
            action.node = null
          }
          if (action.targetList && action.targetList.length > 0) {
            action.targetList = action.targetList.map((uuid) => {
              const node = nodePool.find(uuid)
              const resNode = getSameRouteNodeBetweenScene(node, sceneNode)
              if (resNode) {
                return resNode.uuid
              }
              return null
            })
            action.targetList = action.targetList.filter((uuid) => !!uuid)
            action.paramLabelUI = getParamLabelUI(action.targetList)
          }
          if (
            !action.node &&
            action.targetList &&
            action.targetList.length > 0
          ) {
            action.node = action.targetList[0]
          }
        }
        return action
      }
      const vpConditionConf = getVpConditionConf()
      targetNodes.forEach((node) => {
        const uuid = node.uuid
        copyNodeAst.forEach((event) => {
          const condition = [event.condition[0]]
          const hasCompare = event.condition.some(
            (item) => item.type === 'compare',
          )
          condition.forEach((conditionItem) => {
            if (isVideo(node)) {
              // 音频的开始结束事件复制粘贴到视频节点时,需要把condition.content改成视频的开始结束
              const index = vpConditionConf['audio']
                .map((conf) => conf.content)
                .findIndex((item) => item === conditionItem.content)
              if (index >= 0) {
                conditionItem.content = vpConditionConf['video'][index].content
              }
            } else if (isAudio(node)) {
              // 视频的开始结束事件复制粘贴到音频节点时,需要把condition.content改成音频的开始结束
              const index = vpConditionConf['video']
                .map((conf) => conf.content)
                .findIndex((item) => item === conditionItem.content)
              if (index >= 0) {
                conditionItem.content = vpConditionConf['audio'][index].content
              }
            }
            const content = conditionItem.content
            let has
            if (content) {
              has = hasEvent(content, uuid)
            }
            if (!hasCompare && has && has.status) {
              const targetEvent = has.event
              if (Array.isArray(event?.action)) {
                event?.action.forEach((action) => {
                  action.uuid = generateUUID()
                  if (pasteInSameScene) {
                    targetEvent?.action.push(action)
                  } else {
                    // 跨场景粘贴事件时，如果没有对应的同路径下的响应对象，则去掉该响应动作
                    if (action.node && action.target) {
                      const node = nodePool.find(action.node)
                      const resNode = getSameRouteNodeBetweenScene(
                        node,
                        sceneNode,
                      )
                      if (resNode) {
                        action = replaceTargetNode(action)
                        targetEvent?.action.push(action)
                      } else {
                        if (action.targetList && action.targetList.length > 0) {
                          const hasNode = action.targetList.some((uuid) => {
                            const node = nodePool.find(uuid)
                            const resNode = getSameRouteNodeBetweenScene(
                              node,
                              sceneNode,
                            )
                            return !!resNode
                          })
                          if (hasNode) {
                            action = replaceTargetNode(action)
                            targetEvent?.action.push(action)
                          }
                        }
                      }
                    } else {
                      targetEvent?.action.push(action)
                    }
                  }
                })
              }
            } else {
              const addEvent = _.cloneDeep(event)
              addEvent.id = generateUUID()
              addEvent.condition.forEach((condition) => {
                condition.node = uuid
                if (
                  condition.content === DragConditionType.VPDRAG_END ||
                  condition.content === DragConditionType.VPUNDRAG_END
                ) {
                  const oldNode2Uuid = condition.node2!
                  condition.node2 = generateUUID()
                  const dragNodes = condition.ext
                    ?.dragTargetArea![oldNode2Uuid].map((nodeUuid) =>
                      nodePool.find(nodeUuid),
                    )
                    .map((node) => gameIframe.qcGame.add.clone(node, sceneNode))
                  if (condition.ext?.dragTargetArea) {
                    condition.ext.dragTargetArea = {
                      [condition.node2]:
                        dragNodes?.map((node) => node.uuid) || [],
                    }
                  }
                  if (condition.ext && condition.ext.dragBound) {
                    const oldDragBoundNode = nodePool.find(
                      condition.ext.dragBound,
                    )
                    const dragBoundNode = gameIframe.qcGame.add.clone(
                      oldDragBoundNode,
                      sceneNode,
                    )
                    const dragerNode = nodePool.find(uuid)
                    const firstLevelNode = getFirstLevelNode(dragerNode)
                    const index = sceneNode.getChildIndex(firstLevelNode)
                    sceneNode.setChildIndex(dragBoundNode, index + 1)
                    condition.ext.dragBound = dragBoundNode.uuid
                    // 粘贴事件时有可能目标节点没有Drager脚本
                    let drager = node.getScript('ps.Drager')
                    if (!drager) {
                      drager = node.addScript('ps.Drager')
                    }
                    drager.dragBound = dragBoundNode
                  }
                }
              })
              if (!pasteInSameScene) {
                // 跨场景粘贴事件时，如果没有对应的同路径下的响应对象，则去掉该响应动作
                addEvent.action = addEvent.action.filter((action) => {
                  if (action.node && action.target) {
                    const node = nodePool.find(action.node)
                    const resNode = getSameRouteNodeBetweenScene(
                      node,
                      sceneNode,
                    )
                    if (resNode) {
                      return true
                    }
                    if (action.targetList && action.targetList.length > 0) {
                      const hasNode = action.targetList.some((uuid) => {
                        const node = nodePool.find(uuid)
                        const resNode = getSameRouteNodeBetweenScene(
                          node,
                          sceneNode,
                        )
                        return !!resNode
                      })
                      return hasNode
                    }
                    return false
                  }
                  return true
                })
              }
              addEvent.action.forEach((action) => {
                action.uuid = generateUUID()
                if (action.node && !action.target) {
                  if (!_.isEmpty(action.script)) {
                    // 带有script的响应动作，目标节点有该script才替换node值
                    const node = nodePool.find(uuid)
                    if (node.getScript(action.script)) {
                      action.node = uuid
                    }
                  } else {
                    action.node = uuid
                  }
                }
                replaceTargetNode(action)
              })
              vpAst.push(addEvent)
            }
          })
        })
      })
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
      })
    },
    undo: ({ oldValue }: PasteNodeEventActionValue) => {
      const { sceneNode, targetNodesAst } = oldValue
      let vpAst = getVpAstFromSceneNode(sceneNode) as AstEventItem[]
      Object.entries(targetNodesAst).forEach(([uuid, ast]) => {
        vpAst = vpAst.filter((event) =>
          event.condition.some((condition) => condition.node !== uuid),
        )
        vpAst.push(...ast)
      })
      store.dispatch('workspace/game/visualProgram/saveAST', {
        ast: vpAst,
        sceneNode,
      })
    },
    value,
    methodName: 'getPasteNodeEventAction',
  }
}

export type PasteNodeLayoutStyleActionValue = {
  newValue: {
    // 节点新样式列表
    styles: Record<string, Record<string, any>>
    // 节点原样式列表
    oldStyles: Record<string, Record<string, any>>
    nodes: qc.Node[]
  }
  oldValue: {
    styles: Record<string, Record<string, any>>
    nodes: qc.Node[]
  }
}

export const getPasteNodeLayoutStyleAction = (
  value: PasteNodeLayoutStyleActionValue,
) => {
  return {
    execute: ({ newValue }: PasteNodeLayoutStyleActionValue) => {
      const { styles, oldStyles, nodes } = newValue
      _.each(nodes, (node) => {
        store.dispatch('workspace/game/setStyle', {
          nodes: styles[node.uuid],
          preNodes: oldStyles[node.uuid],
          record: false,
        })
      })
    },
    undo: ({ oldValue }: PasteNodeStyleActionValue) => {
      const { styles, nodes } = oldValue
      _.each(nodes, (node) => {
        store.dispatch('workspace/game/setStyle', {
          nodes: styles[node.uuid],
          record: false,
        })
      })
    },
    value,
    methodName: 'getPasteNodeLayoutStyleAction',
  }
}
type LayerNodeInfo = {
  node: qc.Node
  color: LayerColor
  oldColor: LayerColor
}
export type UpdateLayerColorActionValue = {
  newValue: {
    nodeInfo: LayerNodeInfo[]
  }
  oldValue: {
    nodeInfo: LayerNodeInfo[]
  }
}
export const getUpdateLayerColorAction = (
  value: UpdateLayerColorActionValue,
) => {
  return {
    execute: ({ newValue }: UpdateLayerColorActionValue) => {
      const { nodeInfo } = newValue
      nodeInfo.forEach((item) => {
        const nodes = getDescendants(item.node)
        nodes.forEach((tnode) => {
          const editorData = editorScript.getScript(tnode)
          editorData.$data.layerColor = item.color
        })
      })
      EventBus.$emit(Event.REFRESHLAYER)
    },
    undo: ({ oldValue }: UpdateLayerColorActionValue) => {
      const { nodeInfo } = oldValue
      nodeInfo.forEach((item) => {
        const nodes = getDescendants(item.node)
        nodes.forEach((tnode) => {
          const editorData = editorScript.getScript(tnode)
          editorData.$data.layerColor = item.oldColor
        })
      })
      EventBus.$emit(Event.REFRESHLAYER)
    },
    value,
    methodName: 'getUpdateLayerColorAction',
  }
}
